import { requestSaveCda } from '@/api/cdaApi';
import { uploadFile, getFileById } from '@/api/file/fileApi';
import { INIT_NO_OP_NUM, CDA_FILE_TYPE } from '@/config';
import { setCda, clearCda } from '@/mutation-types';
import kinds from '@/constants/kinds';

const state = {
  cdaId: INIT_NO_OP_NUM,    // id in db
  cdaIndex: INIT_NO_OP_NUM, // id in dropdown list
};

const getters = {
  getCdaId: state => state.cdaId,
  getCdaIndex: state => state.cdaIndex,
  /**
   * Groups cda side bar content by date and kind
   * @returns {object} {2018-10-11: {'procedure':[], 'measures':[], 'diagnosis': []...}, }
   */
  getCdaSideBarContentGrouped (state, getters, rootState) {

    if (getters.getCdaIndex === INIT_NO_OP_NUM) return;

    const ccds = rootState.login.phrData.ccds;
    const cda = rootState.login.phrData.cdas[state.cdaIndex];
    const bdaks = cda.byDateAndKind;
    const measures = (cda.isSaved)?rootState.login.phrData.measures:cda.emeasures;
    const procedures = (cda.isSaved)?rootState.login.phrData.procedures:cda.eprocedures;
    const procmeas = (cda.isSaved)?rootState.login.phrData.procmeas:cda.eprocmeas;
    const diagnosiss = (cda.isSaved)?rootState.login.phrData.diagnosiss:cda.ediagnosiss;
    const vaccines = (cda.isSaved)?rootState.login.phrData.vaccines:cda.evaccines;
    const medications = (cda.isSaved)?rootState.login.phrData.medications:cda.emeds;
    const allergies = (cda.isSaved)?rootState.login.phrData.allergies:cda.eallergies;

    let sideBarContent = {};
    let hasCcd = true;  // flag to indicate whether an entry should be included for display
    let entry = {};
    Object.keys(bdaks).forEach(k => {
      let bdak = bdaks[k];
      if (bdak.lo === null || typeof bdak.lo === 'undefined' ) {
        bdak.lo = 'Undated';
      }
      // create holder for entries of a particular date
      if (sideBarContent[bdak.lo] === null || typeof sideBarContent[bdak.lo] === 'undefined') {
        sideBarContent[bdak.lo] = {};
      }

      entry = {};
      entry.kind = bdak.kind;
      hasCcd = false;
      if (bdak.kind === kinds.E_PROCEDURE || bdak.kind === kinds.PROCEDURE) {
        const ccdId = procedures[bdak.id].ccd;

        if (ccdId) {
          hasCcd = true;
          entry.id = bdak.id;
          entry.cdaIndex = state.cdaIndex;
          entry.ccd = ccdId;
          entry.desc = ccds[ccdId].desc;
          entry = { ...entry, ...procedures[bdak.id] };
          // get list of all corresponding emeasures
          const measIds = procmeas[bdak.id] || [];
          entry.measures = [];
          measIds.forEach(measId => {
            let meas = measures[measId];
            meas.dname = ccds[meas.ccd].desc;
            meas.procCcd = ccdId;
            meas.kind = (bdak.kind === kinds.PROCEDURE)?kinds.MEASURE:kinds.E_MEASURE;
            entry.measures.push(meas);
          });
        }
      } else if (bdak.kind === kinds.E_MEASURE || bdak.kind === kinds.MEASURE) {
        let ccdId = measures[bdak.id].ccd;
        if (ccdId) {
          hasCcd = true;
          entry.dname = ccds[ccdId].desc;
          entry = {...entry, ...measures[bdak.id]};
          entry.weight = measures[bdak.id].weight ? measures[bdak.id].weight : 0;     // if weight is null, set to 0
        }

      } else if (bdak.kind === kinds.E_DIAGNOSIS || bdak.kind === kinds.DIAGNOSIS) {
        // for the 0 in codes[0], because of icd9,10, different codes have same/similar desc; choose first entry
        let ccdId = diagnosiss[bdak.id].codes[0];
        if (ccdId) {
          hasCcd = true;
          entry.desc = ccds[ccdId].desc;
          entry = {...entry, ...diagnosiss[bdak.id]};
        }
      } else if (bdak.kind === kinds.E_MEDICATION || bdak.kind === kinds.MEDICATION) {
        let ccdId = medications[bdak.id].codes[0];
        if (ccdId) {
          hasCcd = true;
          entry.desc = ccds[ccdId].desc;
          entry = {...entry, ...medications[bdak.id]};
        }
      } else if (bdak.kind === kinds.E_VACCINE || bdak.kind === kinds.VACCINE) {
        let ccdId = bdak.kind === kinds.VACCINE?vaccines[bdak.id].codes[0]:vaccines[bdak.id].ccd;
        if (ccdId) {
          hasCcd = true;
          entry.desc = ccds[ccdId].desc;
          entry = {...entry, ...vaccines[bdak.id]};
        }
      } else if (bdak.kind === kinds.E_ALLERGY || bdak.kind === kinds.ALLERGY) {
        let ccdId = allergies[bdak.id].codes[0];
        if (ccdId) {
          hasCcd = true;
          entry.desc = ccds[ccdId].desc;
          entry = {...entry, ...allergies[bdak.id]};
        }
      }
      if (hasCcd) {
        if (!sideBarContent[bdak.lo][bdak.kind]) {
          sideBarContent[bdak.lo][bdak.kind] = [];
        }
        sideBarContent[bdak.lo][bdak.kind].push(entry);

        // sort measures based on weights
        if (bdak.kind === kinds.E_MEASURE || bdak.kind === kinds.MEASURE) {
          sideBarContent[bdak.lo][bdak.kind] = _.orderBy(sideBarContent[bdak.lo][bdak.kind], 'weight', 'desc');
        }
      }
    });
    return sideBarContent;
  },
  getCdaSideBarContentKindAndDate (state, getters, rootState) {
    if (getters.getCdaIndex === INIT_NO_OP_NUM) return;

    const ccds = rootState.login.phrData.ccds;
    const cda = rootState.login.phrData.cdas[state.cdaIndex];
    const bkads = cda.byKindAndDate;
    const measures = (cda.isSaved)?rootState.login.phrData.measures:cda.emeasures;
    const procedures = (cda.isSaved)?rootState.login.phrData.procedures:cda.eprocedures;
    const procmeas = (cda.isSaved)?rootState.login.phrData.procmeas:cda.eprocmeas;
    const diagnosiss = (cda.isSaved)?rootState.login.phrData.diagnosiss:cda.ediagnosiss;
    const vaccines = (cda.isSaved)?rootState.login.phrData.vaccines:cda.evaccines;
    const medications = (cda.isSaved)?rootState.login.phrData.medications:cda.emeds;
    const allergies = (cda.isSaved)?rootState.login.phrData.allergies:cda.eallergies;

    let sideBarContent = {};
    let hasCcd = true;  // flag to indicate whether an entry should be included for display
    let entry = {};
    Object.keys(bkads).forEach(kind => {
      let bkad = bkads[kind];
      sideBarContent[kind]= {};

      entry = {};
      entry.kind = kind;
      sideBarContent.kind = {};

      if (kind === kinds.E_PROCEDURE || kind === kinds.PROCEDURE) {

        bkad.forEach(item => {
          const ccdId = procedures[item.id].ccd;
          if (ccdId) {
            entry = {};
            entry.id = item.id;
            entry.cdaIndex = state.cdaIndex;
            entry.ccd = ccdId;
            entry.desc = ccds[ccdId].desc;
            entry.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry = {...entry, ...procedures[item.id]};

            // get a list of all corresponding measures
            const measIds = procmeas[item.id] || [];
            entry.measures = [];
            measIds.forEach(measId => {
              let meas = measures[measId];
		    /*
	      if(!meas)
		    return;
		    */
              meas.dname = ccds[meas.ccd].desc;
              meas.procCcd = ccdId;
              meas.kind = (kind === kinds.PROCEDURE)?kinds.MEASURE:kinds.E_MEASURE;
              entry.measures.push(meas);
            });

            if (!sideBarContent[kind][entry.lo]) {
              sideBarContent[kind][entry.lo] = [];
            }
            sideBarContent[kind][entry.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_MEASURE || kind === kinds.MEASURE) {
        bkad.forEach(item => {
          let ccdId = measures[item.id].ccd;
          if (ccdId) {
            entry = {};
            entry.lo = item.lo || 'Undated';
            entry.dname = ccds[ccdId].desc;
            entry.kind = kind;
            entry = {...entry, ...measures[item.id]};
            entry.weight = measures[item.id].weight ? measures[item.id].weight : 0;     // if weight is null, set to 0
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
            // todo order inefficient
            sideBarContent[kind][item.lo] = _.orderBy(sideBarContent[kind][item.lo], 'weight', 'desc');
          }
        });

      }
      else if (kind === kinds.E_DIAGNOSIS || kind === kinds.DIAGNOSIS) {
        bkad.forEach(item => {
          let ccdId = diagnosiss[item.id].codes[0];
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = ccds[ccdId].desc;
            entry = {...entry, ...diagnosiss[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_MEDICATION || kind === kinds.MEDICATION) {
        bkad.forEach(item => {
          let ccdId = medications[item.id].codes[0];
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = ccds[ccdId].desc;
            entry = {...entry, ...medications[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_VACCINE || kind === kinds.VACCINE) {
        bkad.forEach(item => {
          let ccdId = item.kind === kinds.VACCINE?vaccines[item.id].codes[0]:vaccines[item.id].ccd;
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = ccds[ccdId].desc;
            entry = {...entry, ...vaccines[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_ALLERGY || kind === kinds.ALLERGY) {
        bkad.forEach(item => {
          let ccdId = allergies[item.id].codes[0];
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = ccds[ccdId].desc;
            entry = {...entry, ...allergies[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }

    });

    return sideBarContent;

  },
  /**
   * returns a list of all cda ids
   */
  getCdaList (state, getters, rootState, rootGetters) {
    // pick id and recordedAt from all data
    let cdaList = [];
    if (rootGetters['login/getAllData'] &&  rootGetters['login/getAllData'].cdas) {
      rootGetters['login/getAllData'].cdas.forEach((cda,index) => {
        cdaList.push({id:cda.id, index, recordedAt: cda.recordedAt, displayName: (cda.name || cda.recordedAt)});
      });
    }
    return cdaList;
  },
  getIndexOfLastCda (state, getters, rootState) {
    return rootState.login.phrData.cdas.length-1;
  },
  getCdaById (state, getters, rootState) {

  },
  isCdaSaved (state,getters,rootState) {
    return (state.cdaIndex === INIT_NO_OP_NUM)
        ? false
        : rootState.login.phrData.cdas[state.cdaIndex].isSaved;
  }
};

const mutations = {
  [setCda] (state, payload) {
    state.cdaId = parseInt(payload.cdaId);
    state.cdaIndex = parseInt(payload.cdaIndex);
  },
  [clearCda] (state) {
    state.cdaId = INIT_NO_OP_NUM;
    state.cdaIndex = INIT_NO_OP_NUM;
  }
};

const actions = {

  uploadCda ({commit, state, rootState}, formData) {
    return new Promise((resolve, reject) => {
      if (formData) {
        uploadFile(CDA_FILE_TYPE, formData)
            .then((response) => {
              const responseData = response.data;
              // update model
              resolve(responseData.id);
            })
            .catch((err) => {
              reject(err);
            });
      } else {
        reject('Please include a CDA file (in .xml format) you want to upload');
      }
    });
  },

  requestCdaById ({commit}, reqData) {
    const {id, index} = reqData;
    return new Promise((resolve, reject) => {
      getFileById(CDA_FILE_TYPE, id)
          .then((cdaDoc) => {
            resolve(cdaDoc);
            commit(setCda, {cdaId: id, cdaIndex: index});
          })
          .catch((err) => {
            reject(err);
          });
    });
  },

  saveCdaById ({commit}, id) {
    return new Promise((resolve, reject) => {
      requestSaveCda(id)
      .then((response)=> {
        const responseData = response.data;
        commit('login/setApplicationModel', responseData.data, {root: true});
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
    });
  },

  clearCda ({commit}) {
    return new Promise((resolve) => {
      commit(clearCda);
      resolve();
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
